import { PLACEMENT_ROLES } from "../pages/constants"

function splitEvery(n, arr) {
  let result = []
  for (let i = 0; i < arr.length; i += n) {
    result.push(arr.slice(i, i + n))
  }
  return result
}

// interface HasPlacements {
//   placements: Array<{
//     x: number;
//     y: number;
//     type: "FEET-ONLY" | "START" | "MIDDLE" | "FINISH";
//     ledPosition: number;
//   }>;
// }

// Color bytes are 2-bit-per-channel RGB packed into the top 6 bits
// (RRGGBB00). The low 2 bits carry LED-position bits 8-9, so every
// palette value must be a multiple of 4.
export const withAliases = (palette) => ({
  ...palette,
  start: palette.START,
  middle: palette.MIDDLE,
  finish: palette.FINISH,
  foot: palette.FOOT,
})

// Decode a wire color byte back to display RGB: each 2-bit channel
// maps to one of four levels (0, 85, 170, 255).
export const colorByteToRgb = (byte) => [
  ((byte >> 6) & 3) * 85,
  ((byte >> 4) & 3) * 85,
  ((byte >> 2) & 3) * 85,
]

const GRAY_NAMES = ["off", "dark gray", "gray", "white"]
const HUE_NAMES = [
  "red",
  "orange",
  "yellow",
  "chartreuse",
  "green",
  "spring green",
  "cyan",
  "azure",
  "blue",
  "violet",
  "magenta",
  "rose",
]

const colorFacts = (byte) => {
  const rgb = colorByteToRgb(byte)
  const [r, g, b] = rgb
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  if (max === min) {
    return { rgb, isGray: true, hue: 0, name: GRAY_NAMES[max / 85] }
  }
  const d = max - min
  let hue
  if (max === r) hue = 60 * ((g - b) / d)
  else if (max === g) hue = 60 * ((b - r) / d + 2)
  else hue = 60 * ((r - g) / d + 4)
  if (hue < 0) hue += 360
  const name =
    (max < 255 ? "dim " : "") +
    (min > 0 ? "pale " : "") +
    HUE_NAMES[Math.round(hue / 30) % 12]
  return { rgb, isGray: false, hue, name }
}

export const describeColorByte = (byte) => colorFacts(byte).name

// Every color the wall can show (one per valid byte), sorted for
// display: rainbow by hue, brightest first, grays at the end
export const COLOR_OPTIONS = Array.from({ length: 64 }, (_, i) => {
  const byte = i * 4
  return { byte, ...colorFacts(byte) }
}).sort((a, b) => a.isGray - b.isGray || a.hue - b.hue || b.byte - a.byte)

export const PALETTE_A = withAliases({
  FOOT: 224,
  START: 48,
  MIDDLE: 60,
  FINISH: 204,
})

// Alternate palette so a second problem is distinguishable on the wall:
// foot red, start blue, middle yellow, finish white. Tune at a real board.
export const PALETTE_B = withAliases({
  FOOT: 192,
  START: 12,
  MIDDLE: 24,
  FINISH: 252,
})

const colors = PALETTE_A

// The header length field is a single byte, so the body (1 + 2 * holds)
// can hold at most 127 placements.
const MAX_PLACEMENTS = 127

/**
 * Compute the messages that light one or more climbs at once on the
 * kilter board. Entries are `{climb, palette}`; the wire format is a
 * flat list of (led, color) pairs, so climbs merge by concatenation.
 * When two climbs share an LED, the earlier entry wins.
 *
 * This is a port of the `prepBytesV2` algorithm
 */
export const getBluetoothMessagesForClimbs = (entries) => {
  const byLed = new Map()
  for (const { climb, palette } of entries) {
    if (!climb) continue
    climb.placements.forEach((placement, i) => {
      const led = placement.led_position
      if (led == null || byLed.has(led)) return
      const placement_role = climb.placement_roles[i]
      const placement_type = PLACEMENT_ROLES[placement_role]?.name
      const color = palette[placement_type]
      if (color == null) return
      byLed.set(led, [led & 255, color + ((led & 768) >> 8)])
    })
  }

  let pairs = [...byLed.values()]
  if (pairs.length > MAX_PLACEMENTS) {
    console.warn(
      `Too many holds to display (${pairs.length}), truncating to ${MAX_PLACEMENTS}`
    )
    pairs = pairs.slice(0, MAX_PLACEMENTS)
  }

  const placements = [
    80, // constant, "changed command to 'P'"
    ...pairs.flat(),
  ]
  const header = [placements.length, checksum(placements)]

  return splitMessages([1, ...header, 2, ...placements, 3])
}

/**
 * Compute the messages that need to be sent to the kilter
 * board to show a single climb.
 *
 * @param climb
 */
export const getBluetoothMessages = (climb) =>
  getBluetoothMessagesForClimbs([{ climb, palette: PALETTE_A }])

/**
 * The kilter board only supports messages of 20 bytes
 * at a time. This method splits a full message into parts
 * of 20 bytes
 *
 * @param buffer
 */
export const splitMessages = (buffer) =>
  splitEvery(20, buffer).map((arr) => new Uint8Array(arr))

/**
 * The second byte of the header, ported from the Java algorithm.
 * A checksum should only contain the placement data.
 *
 * @param list
 */
export const checksum = (list) => {
  let i = 0

  list.forEach((n) => {
    i = (i + n) & 255
  })

  return ~i & 255
}

export const connectToBluetooth = async () => {
  if (!("bluetooth" in navigator)) {
    return
  }

  return navigator.bluetooth
    .requestDevice({
      filters: [
        {
          services: ["4488b571-7806-4df6-bcff-a2897e4953ff"],
        },
      ],
      optionalServices: ["6e400001-b5a3-f393-e0a9-e50e24dcca9e"],
    })
    .then((device) => {
      console.log("> Found " + device.name)
      console.log("Connecting to GATT Server...")
      return device.gatt.connect()
    })
    .then((server) => {
      console.log("GATT Server connected")
      return server.getPrimaryService("6e400001-b5a3-f393-e0a9-e50e24dcca9e")
    })
    .then((service) => {
      console.log("Getting service...")
      return service.getCharacteristic("6e400002-b5a3-f393-e0a9-e50e24dcca9e")
    })
}

async function mapSeries(array, asyncFn) {
  const results = []
  for (const item of array) {
    const result = await asyncFn(item)
    results.push(result)
  }
  return results
}

export const sendClimbsToWall = (characteristic, entries) => {
  return mapSeries(getBluetoothMessagesForClimbs(entries), (msg) =>
    characteristic.writeValue(msg)
  )
}

export const sendClimbToWall = (characteristic, climb) => {
  return sendClimbsToWall(characteristic, [{ climb, palette: PALETTE_A }])
}

/**
 * Sending an empty placement list turns off every LED.
 */
export const clearWall = (characteristic) => {
  return sendClimbsToWall(characteristic, [])
}

export {}
